import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getScheduledReports, deleteScheduledReport } from '../../../services/reportService';

const ScheduledReportsList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScheduledReports();
  }, []);

  const loadScheduledReports = async () => {
    setLoading(true);
    try {
      const result = await getScheduledReports();
      setReports(result?.data || []);
    } catch (error) {
      console.error('Error loading scheduled reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) return;
    
    try {
      await deleteScheduledReport(id);
      setReports(prev => prev?.filter(r => r?.id !== id));
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-12">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin">
            <Icon name="Loader2" size={48} className="text-primary" />
          </div>
          <p className="text-muted-foreground">Loading scheduled reports...</p>
        </div>
      </div>
    );
  }

  if (reports?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Icon name="Calendar" size={48} className="text-muted-foreground" />
          <h3 className="text-xl font-bold text-foreground">No Scheduled Reports</h3>
          <p className="text-muted-foreground text-center">
            You haven't scheduled any reports yet. Create a report and schedule email delivery to get started.
          </p>
        </div>
      </div>
    );
  }

  const getFrequencyBadge = (frequency) => {
    const colors = {
      once: 'bg-blue-100 text-blue-700',
      daily: 'bg-green-100 text-green-700',
      weekly: 'bg-purple-100 text-purple-700',
      monthly: 'bg-orange-100 text-orange-700'
    };
    return colors?.[frequency] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      {reports?.map((report) => {
        const recipients = JSON.parse(report?.recipients || '[]');
        const filters = JSON.parse(report?.filters || '{}');
        
        return (
          <div key={report?.id} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold text-foreground">{report?.reportName}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFrequencyBadge(report?.frequency)}`}>
                    {report?.frequency}
                  </span>
                  {report?.isActive && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  )}
                </div>
                
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={16} />
                    <span className="capitalize">Report Type: {report?.reportType}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} />
                    <span>Recipients: {recipients?.join(', ')}</span>
                  </div>
                  
                  {report?.scheduleTime && (
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} />
                      <span>Scheduled at: {report?.scheduleTime}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Icon name="Filter" size={16} />
                    <span>
                      Filters: Position ({filters?.position}), League ({filters?.league}), Timeframe ({filters?.timeframe})
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} />
                    <span>Created: {new Date(report?.createdAt)?.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(report?.id)}
                iconName="Trash2"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ScheduledReportsList;